/**
 * AI Task Button
 *
 * A dropdown button that allows users to run AI tasks on text content.
 * Supports content versioning for reverting changes.
 */

import { useState, useCallback } from 'react';
import {
  Button,
  Dropdown,
  Spin,
  message,
  Modal,
  Typography,
  Space,
} from 'antd';
import {
  RobotOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { aiApi, type AITask } from '@api/ai';
import type { MenuProps } from 'antd';

const { Text, Paragraph } = Typography;

export interface AITaskButtonProps {
  /** Current text content to process */
  content: string;
  /** Callback when content should be replaced */
  onContentChange: (newContent: string) => void;
  /** Button size */
  size?: 'small' | 'middle' | 'large';
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Button type */
  type?: 'default' | 'text' | 'link' | 'primary';
  /** Additional className */
  className?: string;
}

interface ContentVersion {
  content: string;
  timestamp: Date;
  taskName?: string;
}

/**
 * AI Task Button Component
 *
 * Use this button to add AI task functionality to any text input or editor.
 * It maintains a history of content versions for easy reverting.
 */
export const AITaskButton: React.FC<AITaskButtonProps> = ({
  content,
  onContentChange,
  size = 'small',
  disabled = false,
  type = 'text',
  className,
}) => {
  const [executing, setExecuting] = useState<string | null>(null);
  const [versions, setVersions] = useState<ContentVersion[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Fetch available tasks
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['ai-tasks'],
    queryFn: () => aiApi.listTasks(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const tasks = tasksData?.items?.filter(t => t.is_enabled) || [];

  // Execute an AI task
  const executeTask = useCallback(async (task: AITask) => {
    if (!content.trim()) {
      message.warning('Please enter some content first');
      return;
    }

    setExecuting(task.task_key);

    try {
      // Save current version before modifying
      setVersions(prev => [{
        content,
        timestamp: new Date(),
        taskName: `Before: ${task.display_name}`,
      }, ...prev.slice(0, 9)]); // Keep last 10 versions

      const result = await aiApi.executeTask(task.task_key, {
        variables: { content },
      });

      if (result.success && result.content) {
        onContentChange(result.content);
        message.success(`Applied: ${task.display_name}`);
      } else {
        message.error(result.error || 'Task execution failed');
        // Restore previous version on error
        setVersions(prev => prev.slice(1));
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to execute task';
      message.error(errorMsg);
      // Restore previous version on error
      setVersions(prev => prev.slice(1));
    } finally {
      setExecuting(null);
    }
  }, [content, onContentChange]);

  // Revert to a previous version
  const revertToVersion = useCallback((version: ContentVersion) => {
    onContentChange(version.content);
    setHistoryOpen(false);
    message.success('Reverted to previous version');
  }, [onContentChange]);

  // Group tasks by category
  const tasksByCategory = tasks.reduce((acc, task) => {
    const cat = task.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(task);
    return acc;
  }, {} as Record<string, AITask[]>);

  // Build menu items
  const menuItems: MenuProps['items'] = [];

  // Add task categories
  Object.entries(tasksByCategory).forEach(([category, categoryTasks]) => {
    menuItems.push({
      key: `group-${category}`,
      type: 'group',
      label: category.charAt(0).toUpperCase() + category.slice(1),
      children: categoryTasks.map(task => ({
        key: task.task_key,
        icon: executing === task.task_key ? <Spin size="small" /> : <ThunderboltOutlined />,
        label: (
          <div>
            <div>{task.display_name}</div>
            {task.description && (
              <Text type="secondary" style={{ fontSize: 11 }}>{task.description}</Text>
            )}
          </div>
        ),
        onClick: () => executeTask(task),
        disabled: executing !== null,
      })),
    });
  });

  // Add divider and history option if there are versions
  if (versions.length > 0) {
    menuItems.push({ type: 'divider' });
    menuItems.push({
      key: 'history',
      icon: <HistoryOutlined />,
      label: `Version History (${versions.length})`,
      onClick: () => setHistoryOpen(true),
    });
  }

  // If no tasks available, show message
  if (tasks.length === 0 && !tasksLoading) {
    menuItems.push({
      key: 'no-tasks',
      label: (
        <Text type="secondary" style={{ padding: 8 }}>
          No AI tasks available. Configure tasks in Settings.
        </Text>
      ),
      disabled: true,
    });
  }

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        disabled={disabled || tasksLoading}
        placement="bottomRight"
      >
        <Button
          type={type}
          size={size}
          icon={tasksLoading ? <Spin size="small" /> : <RobotOutlined />}
          className={className}
          loading={executing !== null}
        >
          {size !== 'small' && 'AI'}
        </Button>
      </Dropdown>

      {/* Version History Modal */}
      <Modal
        title="Content Version History"
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
        width={600}
      >
        {versions.length === 0 ? (
          <Text type="secondary">No version history available</Text>
        ) : (
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            {versions.map((version, index) => (
              <div
                key={index}
                style={{
                  padding: 12,
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  marginBottom: 8,
                }}
              >
                <Space style={{ marginBottom: 8 }}>
                  <Text strong>{version.taskName || 'Manual edit'}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {version.timestamp.toLocaleTimeString()}
                  </Text>
                </Space>
                <Paragraph
                  ellipsis={{ rows: 3, expandable: true }}
                  style={{
                    background: '#fafafa',
                    padding: 8,
                    borderRadius: 4,
                    margin: 0,
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {version.content}
                </Paragraph>
                <Button
                  size="small"
                  type="link"
                  onClick={() => revertToVersion(version)}
                  style={{ marginTop: 8, padding: 0 }}
                >
                  Restore this version
                </Button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
};

export default AITaskButton;
